import { useState, useEffect, useMemo } from 'react';
import { axiosInstance } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, FolderTree, Pin, PinOff, GripVertical, FileText, Download, Upload, Archive, ArchiveRestore, Search } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function SortableItem({ category, handleTogglePin, handleToggleArchive, handleEdit, handleDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: category.id, disabled: !category.is_pinned });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isArchived = category.is_archived;

  return (
    <div ref={setNodeRef} style={style}>
      <Card className={`shadow-lg hover:shadow-xl transition-all border-0 overflow-hidden ${isArchived ? 'opacity-60' : ''}`} data-testid={`category-card-${category.name}`}>
        <CardHeader className={`${isArchived ? 'bg-gradient-to-br from-gray-400 to-gray-500' : 'bg-gradient-to-br from-purple-500 to-pink-500'} text-white pb-3`}>
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm sm:text-base font-semibold flex items-center flex-1 min-w-0">
              {category.is_pinned && !isArchived && (
                <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing mr-1 sm:mr-2 flex-shrink-0">
                  <GripVertical className="w-3 h-3" />
                </div>
              )}
              {category.is_pinned && <Pin className="w-3 h-3 mr-1 flex-shrink-0" />}
              {isArchived && <Archive className="w-3 h-3 mr-1 flex-shrink-0" />}
              <span className="truncate">{category.name}</span>
            </CardTitle>
            <div className="flex space-x-0.5 sm:space-x-1 flex-shrink-0">
              <Button 
                size="icon" 
                variant="ghost" 
                className="h-7 w-7 sm:h-8 sm:w-8 hover:bg-white/20"
                onClick={() => handleToggleArchive(category.id)}
                data-testid={`archive-category-${category.name}`}
                title={isArchived ? 'Arşivden çıkar' : 'Arşivle'}
              >
                {isArchived ? <ArchiveRestore className="w-3 h-3" /> : <Archive className="w-3 h-3" />}
              </Button>
              <Button 
                size="icon" 
                variant="ghost" 
                className="h-7 w-7 sm:h-8 sm:w-8 hover:bg-white/20"
                onClick={() => handleTogglePin(category.id)}
                data-testid={`pin-category-${category.name}`}
                title={category.is_pinned ? 'Sabitlemeyi kaldır' : 'Sabitle'}
              >
                {category.is_pinned ? <PinOff className="w-3 h-3" /> : <Pin className="w-3 h-3" />}
              </Button>
              <Button 
                size="icon" 
                variant="ghost" 
                className="h-7 w-7 sm:h-8 sm:w-8 hover:bg-white/20"
                onClick={() => handleEdit(category)}
                data-testid={`edit-category-${category.name}`}
              >
                <Edit2 className="w-3 h-3" />
              </Button>
              <Button 
                size="icon" 
                variant="ghost" 
                className="h-7 w-7 sm:h-8 sm:w-8 hover:bg-red-500/20"
                onClick={() => handleDelete(category.id)}
                data-testid={`delete-category-${category.name}`}
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-3">
          <a 
            href={category.link} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-xs text-purple-600 hover:text-purple-700 break-all"
          >
            {category.link}
          </a>
        </CardContent>
      </Card>
    </div>
  );
}

export default function Categories({ selectedTeam }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    link: '',
    channel_id: ''
  });
  
  // CSV Bulk Upload states
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  
  // Archive filter state
  const [showArchived, setShowArchived] = useState(false);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Filtered categories based on search
  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) return categories;
    const query = searchQuery.toLowerCase().trim();
    return categories.filter(category => 
      category.name.toLowerCase().includes(query) ||
      (category.link && category.link.toLowerCase().includes(query))
    );
  }, [categories, searchQuery]);

  useEffect(() => {
    if (selectedTeam) {
      loadCategories();
    }
  }, [selectedTeam, showArchived]);

  const loadCategories = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/teams/${selectedTeam}/categories${showArchived ? '?include_archived=true' : ''}`);
      setCategories(res.data);
    } catch (err) {
      toast.error('Kategoriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await axiosInstance.put(`/teams/${selectedTeam}/categories/${editingCategory.id}`, formData);
        toast.success('Kategori güncellendi');
      } else {
        await axiosInstance.post(`/teams/${selectedTeam}/categories`, formData);
        toast.success('Kategori eklendi');
      }
      setDialogOpen(false);
      setFormData({ name: '', link: '' });
      setEditingCategory(null);
      loadCategories();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'İşlem başarısız');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      link: category.link,
      channel_id: category.channel_id || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm('Bu kategoriyi silmek istediğinizden emin misiniz?')) return;
    
    try {
      await axiosInstance.delete(`/teams/${selectedTeam}/categories/${categoryId}`);
      toast.success('Kategori silindi');
      loadCategories();
    } catch (err) {
      toast.error('Kategori silinemedi');
    }
  };

  const openNewDialog = () => {
    setEditingCategory(null);
    setFormData({ name: '', link: '', channel_id: '' });
    setDialogOpen(true);
  };

  const handleTogglePin = async (categoryId) => {
    try {
      const res = await axiosInstance.post(`/teams/${selectedTeam}/categories/${categoryId}/toggle-pin`);
      toast.success(res.data.is_pinned ? 'Kategori sabitlendi' : 'Sabitleme kaldırıldı');
      loadCategories();
    } catch (err) {
      toast.error('İşlem başarısız');
    }
  };

  const handleToggleArchive = async (categoryId) => {
    try {
      const res = await axiosInstance.post(`/teams/${selectedTeam}/categories/${categoryId}/toggle-archive`);
      toast.success(res.data.message);
      loadCategories();
    } catch (err) {
      toast.error('İşlem başarısız');
    }
  };

  // CSV Bulk Upload functions
  const handleClearAllCategories = async () => {
    if (!window.confirm('⚠️ DİKKAT: Tüm kategoriler silinecek! Bu işlem geri alınamaz. Devam etmek istiyor musunuz?')) {
      return;
    }

    if (!window.confirm('🚨 SON UYARI: Bu işlem TÜM kategorilerinizi kalıcı olarak silecek. Emin misiniz?')) {
      return;
    }

    try {
      const res = await axiosInstance.post(`/teams/${selectedTeam}/admin/clear-categories`);
      toast.success(res.data.message || 'Tüm kategoriler temizlendi');
      loadCategories();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Temizleme başarısız');
    }
  };

  const handleCsvFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Sadece CSV dosyaları desteklenmektedir');
      return;
    }

    setCsvFile(file);
    setPreviewLoading(true);
    setPreviewData([]);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await axiosInstance.post(`/teams/${selectedTeam}/categories/bulk-upload-preview`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setPreviewData(res.data.preview || []);
      toast.success(`${res.data.total} kayıt önizlendi`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Önizleme başarısız');
      setCsvFile(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleBulkImport = async () => {
    if (!csvFile) return;

    setImportLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', csvFile);

      const res = await axiosInstance.post(`/teams/${selectedTeam}/categories/bulk-upload-confirm`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(`${res.data.created} yeni kategori eklendi, ${res.data.updated} kategori güncellendi`);
      setBulkDialogOpen(false);
      setCsvFile(null);
      setPreviewData([]);
      loadCategories();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'İçe aktarma başarısız');
    } finally {
      setImportLoading(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await axiosInstance.get(`/teams/${selectedTeam}/categories/csv-template`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'categories_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Şablon indirildi');
    } catch (err) {
      toast.error('Şablon indirilemedi');
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = categories.findIndex((c) => c.id === active.id);
      const newIndex = categories.findIndex((c) => c.id === over.id);

      const newCategories = arrayMove(categories, oldIndex, newIndex);
      setCategories(newCategories);

      // Send all items with their order
      const itemsToUpdate = newCategories.map((item, index) => ({
        id: item.id,
        order: index,
        ...(item.is_pinned && { pin_order: newCategories.filter(c => c.is_pinned).indexOf(item) })
      }));

      try {
        await axiosInstance.post(`/teams/${selectedTeam}/categories/reorder`, {
          items: itemsToUpdate
        });
        toast.success('Sıralama kaydedildi');
      } catch (err) {
        toast.error('Sıralama kaydedilemedi');
        loadCategories(); // Revert on error
      }
    }
  };

  if (!selectedTeam) {
    return (
      <div className="flex items-center justify-center h-full">
        <Card className="w-96 text-center overflow-hidden">
          <CardContent className="pt-12 pb-12">
            <FolderTree className="w-16 h-16 mx-auto mb-4 text-purple-500" />
            <h2 className="text-2xl font-bold mb-2">Mağaza Seçin</h2>
            <p className="text-gray-600">Kategorileri yönetmek için bir mağaza seçin.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="categories-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Kategoriler</h1>
          <p className="text-gray-600">Kategorilerinizi yönetin</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Kategori ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-full sm:w-48"
              data-testid="category-search-input"
            />
          </div>
          <Button 
            variant={showArchived ? "default" : "outline"}
            className={showArchived ? "bg-gray-600 hover:bg-gray-700" : "border-gray-400 text-gray-600 hover:bg-gray-50"}
            onClick={() => setShowArchived(!showArchived)}
            data-testid="toggle-archived-btn"
          >
            {showArchived ? <ArchiveRestore className="w-5 h-5 mr-2" /> : <Archive className="w-5 h-5 mr-2" />}
            {showArchived ? 'Arşivi Gizle' : 'Arşivi Göster'}
          </Button>
          <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                variant="outline"
                className="border-purple-500 text-purple-600 hover:bg-purple-50 w-full sm:w-auto"
              >
                <FileText className="w-5 h-5 mr-2" />
                Toplu Yükle
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Toplu Kategori Yükleme</DialogTitle>
                <DialogDescription>
                  CSV dosyası ile birden fazla kategori ekleyin veya güncelleyin
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-2xl border border-blue-200">
                  <div className="flex items-center gap-2">
                    <Download className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="text-sm font-medium text-blue-900">Örnek CSV Şablonu</p>
                      <p className="text-xs text-blue-700">Dosya formatını görmek için şablonu indirin</p>
                    </div>
                  </div>
                  <Button 
                    onClick={downloadTemplate}
                    variant="outline"
                    size="sm"
                    className="border-blue-600 text-blue-600 hover:bg-blue-100"
                  >
                    İndir
                  </Button>
                </div>

                <div className="flex items-center justify-between p-4 bg-red-50 rounded-2xl border border-red-200">
                  <div className="flex items-center gap-2">
                    <Trash2 className="w-5 h-5 text-red-600" />
                    <div>
                      <p className="text-sm font-medium text-red-900">Tüm Kategorileri Temizle</p>
                      <p className="text-xs text-red-700">Bozuk veri varsa tüm kategorileri silebilirsiniz</p>
                    </div>
                  </div>
                  <Button 
                    onClick={handleClearAllCategories}
                    variant="outline"
                    size="sm"
                    className="border-red-600 text-red-600 hover:bg-red-100"
                  >
                    Temizle
                  </Button>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="csv_upload">CSV Dosyası Seçin</Label>
                  <label
                    htmlFor="csv_upload"
                    className="flex items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-2xl cursor-pointer hover:border-purple-500 transition-colors bg-gray-50"
                  >
                    <div className="text-center">
                      <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                      <p className="text-sm text-gray-600">
                        {csvFile ? csvFile.name : 'CSV dosyası seçin'}
                      </p>
                      {previewLoading && <p className="text-xs text-purple-600 mt-1">Yükleniyor...</p>}
                    </div>
                  </label>
                  <input
                    id="csv_upload"
                    type="file"
                    accept=".csv"
                    onChange={handleCsvFileSelect}
                    className="hidden"
                    disabled={previewLoading}
                  />
                </div>

                {previewData.length > 0 && (
                  <div className="space-y-2">
                    <Label>Önizleme ({previewData.length} kayıt)</Label>
                    <div className="border rounded-2xl overflow-hidden">
                      <div className="max-h-96 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-100 sticky top-0">
                            <tr>
                              <th className="px-4 py-2 text-left">Durum</th>
                              <th className="px-4 py-2 text-left">Kategori Adı</th>
                              <th className="px-4 py-2 text-left">Link</th>
                            </tr>
                          </thead>
                          <tbody>
                            {previewData.map((item, index) => (
                              <tr key={index} className="border-t hover:bg-gray-50">
                                <td className="px-4 py-2">
                                  <span className={`text-xs px-2 py-1 rounded ${
                                    item.status === 'new' 
                                      ? 'bg-green-100 text-green-800' 
                                      : 'bg-blue-100 text-blue-800'
                                  }`}>
                                    {item.status === 'new' ? 'Yeni' : 'Güncelleme'}
                                  </span>
                                </td>
                                <td className="px-4 py-2 font-medium">{item.name}</td>
                                <td className="px-4 py-2 text-xs truncate max-w-xs">{item.link}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setBulkDialogOpen(false);
                    setCsvFile(null);
                    setPreviewData([]);
                  }}
                >
                  İptal
                </Button>
                <Button 
                  onClick={handleBulkImport}
                  disabled={!csvFile || previewData.length === 0 || importLoading}
                  className="bg-gradient-to-r from-purple-500 to-pink-500"
                >
                  {importLoading ? 'İçe Aktarılıyor...' : `${previewData.length} Kayıt İçe Aktar`}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                onClick={openNewDialog}
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-lg w-full sm:w-auto"
                data-testid="create-category-button"
              >
                <Plus className="w-5 h-5 mr-2" />
                Yeni Kategori
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="category-dialog">
              <DialogHeader>
                <DialogTitle>{editingCategory ? 'Kategoriyi Düzenle' : 'Yeni Kategori Ekle'}</DialogTitle>
                <DialogDescription>Kategori bilgilerini girin</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit}>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Kategori Adı *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="DRESS"
                      required
                      data-testid="category-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="link">Link *</Label>
                    <Input
                      id="link"
                      value={formData.link}
                      onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                      placeholder="https://t.me/delfiee_dress"
                      required
                      data-testid="category-link-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="channel_id">Telegram Kanal ID</Label>
                    <Input
                      id="channel_id"
                      value={formData.channel_id}
                      onChange={(e) => setFormData({ ...formData, channel_id: e.target.value })}
                      placeholder="-1001234567890"
                    />
                    <p className="text-xs text-gray-500">Kategorinin kendi telegram kanalı ID'si (opsiyonel)</p>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="submit" className="bg-gradient-to-r from-purple-500 to-pink-500" data-testid="save-category-button">
                    {editingCategory ? 'Güncelle' : 'Ekle'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      ) : categories.length === 0 ? (
        <Card className="text-center py-12 overflow-hidden">
          <CardContent>
            <FolderTree className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-xl font-semibold mb-2">Hiç kategori yok</h3>
            <p className="text-gray-600 mb-4">Yeni bir kategori ekleyerek başlayın</p>
          </CardContent>
        </Card>
      ) : filteredCategories.length === 0 ? (
        <Card className="text-center py-12 overflow-hidden">
          <CardContent>
            <Search className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-xl font-semibold mb-2">Sonuç bulunamadı</h3>
            <p className="text-gray-600 mb-4">"{searchQuery}" için eşleşen kategori yok</p>
          </CardContent>
        </Card>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={filteredCategories.map(c => c.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {filteredCategories.map(category => (
                <SortableItem
                  key={category.id}
                  category={category}
                  handleTogglePin={handleTogglePin}
                  handleToggleArchive={handleToggleArchive}
                  handleEdit={handleEdit}
                  handleDelete={handleDelete}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}
